import { useEffect, useCallback } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transformKeys } from '../services/transformers';
import type { CallHistoryItem } from '../types';

export type CallWebSocketEvent = Partial<CallHistoryItem> & {
  id: string;
};

interface UseCallWebSocketOptions {
  enabled?: boolean;
  onEvent?: (event: CallWebSocketEvent) => void;
}

/**
 * Subscribe to call stream events for a single call id.
 */
export function useCallWebSocket(
  callId?: string | null,
  options: UseCallWebSocketOptions = {},
) {
  const { state, addObserver, removeObserver } = useWebSocket();
  const { onEvent, enabled = true } = options;

  const handleEvent = useCallback(
    (data: CallWebSocketEvent, action: string) => {
      if (action !== 'update' && action !== 'create') {
        return;
      }

      const normalized = transformKeys<CallWebSocketEvent>(data);
      if (callId && normalized.id !== callId) {
        return;
      }
      onEvent?.(normalized);
    },
    [callId, onEvent],
  );

  useEffect(() => {
    if (!enabled || !callId || !state.isConnected || !onEvent) {
      return;
    }

    const observerId = addObserver(
      'call',
      { data: { call_id: callId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
      },
    );

    return () => {
      removeObserver(observerId);
    };
  }, [enabled, callId, state.isConnected, addObserver, removeObserver, handleEvent, onEvent]);

  return {
    isConnected: Boolean(callId) && state.isConnected,
  };
}

export default useCallWebSocket;
